import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { endOfWeek, format, startOfWeek } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import {
  TIME_CLOCK_ENTRIES,
  TIME_CLOCK_USER_SUMMARIES,
} from '../graphql/time-clock';
import { exportHourlyPayrollToExcel } from '../utils/hourlyPayrollExcelExport';

const REMOTE_ENTRY_LABEL = 'Remote Entry';
const EMPLOYEE_SORT_OPTIONS = {
  RECENT: 'recent',
  ALPHABETICAL: 'alphabetical',
  HOURS: 'hours',
};
const AREA_LABELS = {
  KIAWAH: 'Kiawah',
  ISLE_OF_PALMS: 'Isle of Palms',
  ISLE_OF_PALMS_WILD_DUNES: 'Isle of Palms - Wild Dunes',
  SEABROOK_ISLAND: 'Seabrook Island',
};
const PAY_PERIOD_WEEK_OPTIONS = { weekStartsOn: 1 };
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const money = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value));
};

const hoursLabel = (minutes) => {
  const totalMinutes = Math.max(0, Math.round(Number(minutes || 0)));
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const normalizeAreas = (areas) => (Array.isArray(areas) ? areas.filter(Boolean) : []);

const areaLabel = (area) => AREA_LABELS[area] || String(area || '').replace(/_/g, ' ');

const areasLabel = (areas) => {
  const normalizedAreas = normalizeAreas(areas);
  if (normalizedAreas.length === 0) return 'Unassigned';
  return normalizedAreas.map(areaLabel).join(', ');
};

const dateTimeLabel = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return format(date, 'MMM d, h:mm a');
};

const periodBounds = (startDate, endDate) => ({
  start: new Date(`${startDate}T00:00:00`),
  end: new Date(`${endDate}T23:59:59.999`),
});

const clippedMinutes = (entry, startDate, endDate) => {
  if (!entry.clockedInAt || !entry.clockedOutAt) return 0;
  const { start, end } = periodBounds(startDate, endDate);
  const clockIn = new Date(entry.clockedInAt);
  const clockOut = new Date(entry.clockedOutAt);
  if (Number.isNaN(clockIn.getTime()) || Number.isNaN(clockOut.getTime())) return 0;
  const clippedStart = clockIn < start ? start : clockIn;
  const clippedEnd = clockOut > end ? end : clockOut;
  if (clippedEnd <= clippedStart) return 0;
  return Math.round((clippedEnd.getTime() - clippedStart.getTime()) / 60000);
};

const entryFlags = (entry) => {
  const flags = [];
  if (entry.status === 'ACTIVE') flags.push('Open');
  if (entry.clockInOffline || entry.clockOutOffline) flags.push('Offline');
  if (entry.clockInOutsideRadius || entry.clockOutOutsideRadius) flags.push('Outside radius');
  if (entry.isEdited) flags.push('Edited');
  if (entry.notes?.trim()) flags.push('Note');
  return flags;
};

const entryUpdatedTimestamp = (entry) => {
  const timestamps = [
    entry?.updatedAt,
    entry?.editedAt,
    entry?.clockedOutAt,
    entry?.clockedInAt,
  ].map((value) => {
    if (!value) return 0;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  });

  return Math.max(...timestamps, 0);
};

const officeLabel = (entry) => {
  if (entry.clockInOutsideRadius || entry.clockOutOutsideRadius) return REMOTE_ENTRY_LABEL;
  return entry.clockInOfficeName || entry.clockOutOfficeName || '-';
};

function HourlyPayroll({ printView = false }) {
  const [searchParams] = useSearchParams();
  const requestedStartDate = searchParams.get('startDate');
  const requestedEndDate = searchParams.get('endDate');
  const requestedSort = searchParams.get('sort');
  const [startDate, setStartDate] = useState(() => (
    printView && DATE_ONLY_PATTERN.test(requestedStartDate || '')
      ? requestedStartDate
      : format(startOfWeek(new Date(), PAY_PERIOD_WEEK_OPTIONS), 'yyyy-MM-dd')
  ));
  const [endDate, setEndDate] = useState(() => (
    printView && DATE_ONLY_PATTERN.test(requestedEndDate || '')
      ? requestedEndDate
      : format(endOfWeek(new Date(), PAY_PERIOD_WEEK_OPTIONS), 'yyyy-MM-dd')
  ));
  const [showDetails, setShowDetails] = useState(true);
  const [employeeSort, setEmployeeSort] = useState(() => (
    printView && Object.values(EMPLOYEE_SORT_OPTIONS).includes(requestedSort)
      ? requestedSort
      : EMPLOYEE_SORT_OPTIONS.RECENT
  ));

  const { data: summariesData, loading: summariesLoading, refetch: refetchSummaries } = useQuery(
    TIME_CLOCK_USER_SUMMARIES,
    {
      variables: { startDate, endDate },
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data: entriesData, loading: entriesLoading, refetch: refetchEntries } = useQuery(
    TIME_CLOCK_ENTRIES,
    {
      variables: { input: { startDate, endDate } },
      fetchPolicy: 'cache-and-network',
    },
  );

  const summaries = useMemo(
    () => summariesData?.timeClockUserSummaries || [],
    [summariesData],
  );
  const entries = useMemo(
    () => entriesData?.timeClockEntries || [],
    [entriesData],
  );

  const report = useMemo(() => {
    const completedEntries = entries.filter((entry) => entry.status !== 'ACTIVE' && entry.clockedOutAt);
    const openEntries = entries.filter((entry) => entry.status === 'ACTIVE' || !entry.clockedOutAt);
    const flaggedEntries = entries.filter((entry) => entryFlags(entry).length > 0);
    const summariesByUser = new Map(summaries.map((summary) => [summary.userId, summary]));
    const rowsByUser = new Map();

    summaries.forEach((summary) => {
      rowsByUser.set(summary.userId, {
        userId: summary.userId,
        userName: summary.userName,
        role: summary.role,
        areas: normalizeAreas(summary.areas),
        hourlyRate: summary.hourlyRate,
        totalMinutes: 0,
        totalCost: 0,
        completedEntries: 0,
        exceptionCount: summary.flaggedEntriesCount || 0,
        missingRate: summary.hourlyRate === null || summary.hourlyRate === undefined,
        isCurrentlyClockedIn: summary.isCurrentlyClockedIn,
      });
    });

    completedEntries.forEach((entry) => {
      const summary = summariesByUser.get(entry.userId);
      const userName = summary?.userName || `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`.trim() || 'Unknown';
      const hourlyRate = summary?.hourlyRate ?? entry.user?.timeClockHourlyRate ?? null;
      const minutes = clippedMinutes(entry, startDate, endDate);
      const existing = rowsByUser.get(entry.userId) || {
        userId: entry.userId,
        userName,
        role: summary?.role || entry.user?.role || 'USER',
        areas: normalizeAreas(summary?.areas || entry.user?.areas),
        hourlyRate,
        totalMinutes: 0,
        totalCost: 0,
        completedEntries: 0,
        exceptionCount: 0,
        missingRate: hourlyRate === null || hourlyRate === undefined,
        isCurrentlyClockedIn: false,
      };
      existing.totalMinutes += minutes;
      existing.completedEntries += 1;
      existing.hourlyRate = hourlyRate;
      existing.missingRate = hourlyRate === null || hourlyRate === undefined;
      existing.totalCost = existing.missingRate ? null : Number(((existing.totalMinutes / 60) * Number(hourlyRate)).toFixed(2));
      rowsByUser.set(entry.userId, existing);
    });

    const employeeRows = Array.from(rowsByUser.values()).sort((a, b) => a.userName.localeCompare(b.userName));
    const totalMinutes = employeeRows.reduce((sum, row) => sum + row.totalMinutes, 0);
    const totalCost = employeeRows.reduce((sum, row) => sum + (row.totalCost || 0), 0);

    return {
      employeeRows,
      completedEntries,
      openEntries,
      flaggedEntries,
      totalMinutes,
      totalCost,
      missingRateCount: employeeRows.filter((row) => row.missingRate && row.totalMinutes > 0).length,
    };
  }, [entries, endDate, startDate, summaries]);

  const isLoading = summariesLoading || entriesLoading;

  const latestEntryTimestampByUserId = useMemo(() => {
    const timestamps = new Map();
    entries.forEach((entry) => {
      const timestamp = entryUpdatedTimestamp(entry);
      const userId = String(entry.userId || '');
      if (!userId) return;
      if (timestamp > (timestamps.get(userId) || 0)) {
        timestamps.set(userId, timestamp);
      }
    });
    return timestamps;
  }, [entries]);

  const sortedEmployeeRows = useMemo(() => {
    const rows = [...report.employeeRows];

    rows.sort((a, b) => {
      const nameCompare = a.userName.localeCompare(b.userName);

      if (employeeSort === EMPLOYEE_SORT_OPTIONS.ALPHABETICAL) {
        return nameCompare;
      }

      if (employeeSort === EMPLOYEE_SORT_OPTIONS.HOURS) {
        const hoursCompare = Number(b.totalMinutes || 0) - Number(a.totalMinutes || 0);
        return hoursCompare || nameCompare;
      }

      const aTimestamp = latestEntryTimestampByUserId.get(String(a.userId)) || 0;
      const bTimestamp = latestEntryTimestampByUserId.get(String(b.userId)) || 0;
      const recentCompare = bTimestamp - aTimestamp;
      return recentCompare || nameCompare;
    });

    return rows;
  }, [employeeSort, latestEntryTimestampByUserId, report.employeeRows]);

  const handleRefresh = async () => {
    await Promise.all([refetchSummaries(), refetchEntries()]);
  };

  const exportExcel = async () => {
    await exportHourlyPayrollToExcel({
      employeeRows: report.employeeRows,
      startDate,
      endDate,
    });
  };

  const openPrintView = () => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      sort: employeeSort,
    });
    window.open(`/dashboard/hourly-payroll/print?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={printView ? 'hourly-payroll-print-view fixed inset-0 z-[100] overflow-y-auto bg-white' : 'min-h-screen bg-gray-50'}>
      {printView && (
        <style>{`
          @media print {
            @page {
              size: landscape;
              margin: 0.4in;
            }

            body {
              background: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            body * {
              visibility: hidden !important;
            }

            .hourly-payroll-print-view,
            .hourly-payroll-print-view * {
              visibility: visible !important;
            }

            .hourly-payroll-print-view {
              position: absolute !important;
              inset: 0 !important;
              overflow: visible !important;
              min-height: auto !important;
            }

            .hourly-payroll-print-view .no-print {
              display: none !important;
            }

            .hourly-payroll-print-view .shadow-sm {
              box-shadow: none !important;
            }

            .hourly-payroll-print-view table {
              width: 100% !important;
              border-collapse: collapse !important;
              font-size: 10px !important;
            }

            .hourly-payroll-print-view thead {
              display: table-header-group;
            }

            .hourly-payroll-print-view tr {
              break-inside: avoid;
            }

            .hourly-payroll-print-view th,
            .hourly-payroll-print-view td {
              border: 1px solid #e5e7eb !important;
              padding: 6px 8px !important;
            }
          }
        `}</style>
      )}
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Hourly Payroll</h1>
            <p className="mt-1 text-sm text-gray-500">
              {printView
                ? `${format(new Date(`${startDate}T00:00:00`), 'MMM d, yyyy')} – ${format(new Date(`${endDate}T00:00:00`), 'MMM d, yyyy')}`
                : 'Payroll-period totals from completed timeclock entries.'}
            </p>
          </div>
          {printView ? (
            <div className="no-print flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => window.print()}
                disabled={isLoading}
                className="inline-flex items-center rounded-md bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
              >
                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10zM17 9V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Report
              </button>
              <button
                type="button"
                onClick={() => window.close()}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={handleRefresh}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={exportExcel}
                disabled={isLoading}
                className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
              >
                Export XLS
              </button>
              <button
                type="button"
                onClick={openPrintView}
                disabled={isLoading}
                className="inline-flex items-center rounded-md bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
              >
                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10zM17 9V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print View
              </button>
            </div>
          )}
        </div>

        {(report.openEntries.length > 0 || report.missingRateCount > 0) && (
          <section className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-900">Needs Review</h2>
            <div className="mt-2 space-y-1 text-sm text-amber-900">
              {report.openEntries.length > 0 && (
                <p>{report.openEntries.length} open punch{report.openEntries.length === 1 ? '' : 'es'} must be closed before final payroll.</p>
              )}
              {report.missingRateCount > 0 && (
                <p>{report.missingRateCount} employee{report.missingRateCount === 1 ? '' : 's'} with payable hours missing an hourly rate.</p>
              )}
            </div>
          </section>
        )}

        <section className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-4">
          <div className="rounded-lg bg-white p-5 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Payable Hours</div>
            <div className="mt-2 text-3xl font-semibold text-gray-900">{hoursLabel(report.totalMinutes)}</div>
          </div>
          <div className="rounded-lg bg-white p-5 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Gross Pay</div>
            <div className="mt-2 text-3xl font-semibold text-gray-900">{money(report.totalCost)}</div>
          </div>
          <div className="rounded-lg bg-white p-5 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Open Punches</div>
            <div className="mt-2 text-3xl font-semibold text-gray-900">{report.openEntries.length}</div>
          </div>
          <div className="rounded-lg bg-white p-5 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Flagged Entries</div>
            <div className="mt-2 text-3xl font-semibold text-gray-900">{report.flaggedEntries.length}</div>
          </div>
        </section>

        <section className="mb-6 rounded-lg bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Employee Payroll Totals</h2>
            {!printView && (
              <label className="flex items-center gap-2 text-sm text-gray-700" htmlFor="hourlyPayrollEmployeeSort">
                <span className="font-medium">Sort</span>
                <select
                  id="hourlyPayrollEmployeeSort"
                  value={employeeSort}
                  onChange={(event) => setEmployeeSort(event.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value={EMPLOYEE_SORT_OPTIONS.RECENT}>Most recently updated</option>
                  <option value={EMPLOYEE_SORT_OPTIONS.ALPHABETICAL}>Alphabetical</option>
                  <option value={EMPLOYEE_SORT_OPTIONS.HOURS}>Hours: most logged</option>
                </select>
              </label>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Team Member</th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Area</th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Hours</th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Rate</th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Gross Pay</th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Entries</th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Review</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan="7" className="px-5 py-6 text-center text-sm text-gray-500">Loading payroll data...</td>
                  </tr>
                ) : sortedEmployeeRows.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-5 py-6 text-center text-sm text-gray-500">No timeclock users found.</td>
                  </tr>
                ) : (
                  sortedEmployeeRows.map((row) => (
                    <tr key={row.userId}>
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">{row.userName}</td>
                      <td className="px-5 py-3 text-sm text-gray-700">{areasLabel(row.areas)}</td>
                      <td className="px-5 py-3 text-right text-sm text-gray-700">{hoursLabel(row.totalMinutes)}</td>
                      <td className="px-5 py-3 text-right text-sm text-gray-700">{money(row.hourlyRate)}</td>
                      <td className="px-5 py-3 text-right text-sm text-gray-700">{row.missingRate ? '-' : money(row.totalCost)}</td>
                      <td className="px-5 py-3 text-right text-sm text-gray-700">{row.completedEntries}</td>
                      <td className="px-5 py-3 text-right text-sm text-gray-700">
                        <div className="flex justify-end gap-1">
                          {row.isCurrentlyClockedIn && (
                            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Open</span>
                          )}
                          {row.missingRate && row.totalMinutes > 0 && (
                            <span className="rounded bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800">Missing rate</span>
                          )}
                          {!row.isCurrentlyClockedIn && !(row.missingRate && row.totalMinutes > 0) && (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-lg bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Timeclock Entry Details</h2>
            {!printView && (
              <button
                type="button"
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm font-medium text-sky-700 hover:text-sky-900"
              >
                {showDetails ? 'Hide details' : 'Show details'}
              </button>
            )}
          </div>
          {(printView || showDetails) && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Team Member</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Clock In</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Clock Out</th>
                    <th className="px-5 py-3 text-right text-xs font-medium uppercase text-gray-500">Payable Hours</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Office</th>
                    <th className="px-5 py-3 text-left text-xs font-medium uppercase text-gray-500">Flags</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {entries.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-6 text-center text-sm text-gray-500">No entries found for this date range.</td>
                    </tr>
                  ) : (
                    entries.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-5 py-3 text-sm font-medium text-gray-900">
                          {entry.user?.firstName} {entry.user?.lastName}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-700">{dateTimeLabel(entry.clockedInAt)}</td>
                        <td className="px-5 py-3 text-sm text-gray-700">{dateTimeLabel(entry.clockedOutAt)}</td>
                        <td className="px-5 py-3 text-right text-sm text-gray-700">
                          {entry.status === 'ACTIVE' ? '-' : hoursLabel(clippedMinutes(entry, startDate, endDate))}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-700">{officeLabel(entry)}</td>
                        <td className="px-5 py-3 text-sm text-gray-700">
                          <div className="flex flex-wrap gap-1">
                            {entryFlags(entry).length === 0 ? (
                              <span className="text-xs text-gray-400">-</span>
                            ) : (
                              entryFlags(entry).map((flag) => (
                                <span
                                  key={flag}
                                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                                    flag === 'Note'
                                      ? 'bg-sky-100 text-sky-800'
                                      : 'bg-amber-100 text-amber-800'
                                  }`}
                                  title={flag === 'Note' ? entry.notes : undefined}
                                >
                                  {flag}
                                </span>
                              ))
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default HourlyPayroll;
